import moment from 'moment-timezone';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { prepareModelTimeForForm } from './modelTime';
import { prepareSchedule, generateDays } from './schedules';
import { fullName } from './users';
import { PAYROLL_KINDS } from '../../main/settings/commission/constants';
import {
  getBrandSupportedLanguagesOption,
  DEFAULT_AUTO_TIPS_FEE_PERCENT,
  DEFAULT_COMMISSION_SPLIT_FEE_PERCENT,
  SHOP_TYPE,
} from '../../main/shops/constants';
import getLanguageOptionsByCodes from '../LanguageMultiSelect/getLanguageOptionsByCodes';

const makeSchedules = (enabled = false) => {
  const workdays = generateDays(enabled);
  return {
    current: {
      startDate: moment.utc().toDate(),
      workdays,
    },
    future: {
      workdays,
    },
  };
};

export const getinitialValuesFromShop = (shop) => {
  const data = cloneDeep(shop);
  if (!isEmpty(data.photos)) {
    data.avatar = data.photos[0].url;
  }
  if (!isEmpty(data.logo)) {
    data.logo = get(data, 'logo.url');
  }

  if (data.schedules.length === 0) {
    data.schedules = makeSchedules();
  } else {
    data.schedules = prepareSchedule(data.schedules);
  }

  data.timeTrackingEnabled = Boolean(data.timeTrackingEnabled);
  data.payrollKind = data.dailyPayroll ? PAYROLL_KINDS.dailyPayroll : PAYROLL_KINDS.commissionSplits;
  data.payrollEnabled = data.dailyPayroll || data.commissionSplits;

  prepareModelTimeForForm(data, 'advanceBookMins');
  prepareModelTimeForForm(data, 'advanceCancelMins');
  prepareModelTimeForForm(data, 'notifications.reminder.time');
  prepareModelTimeForForm(data, 'customerReminder.barber_reminder.time');
  prepareModelTimeForForm(data, 'customerReminder.reminder.time');

  data.defaultCashDrawerBalance = data.defaultCashDrawerBalance || 0;
  data.barberSpecificGC = data.barberSpecificGC || false;
  const isRental = parseInt(data.kind, 10) === SHOP_TYPE.rental;
  if (!isRental) {
    data.barberSpecificGC = false;
    data.sendGCNotificationsToCommissionBarbers = false;
  }
  data.isFrenchWaiver = data.waiverLanguage === 'fr';
  data.payoutDiscountSplit = get(data, 'settings.payoutDiscountSplit');

  data.brandName = data.brand ? data.brand.name : '';
  const brandSupportedLanguages = getLanguageOptionsByCodes(get(data, 'brand.settings.supportedLanguages'));

  data.brandSupportedLanguages = brandSupportedLanguages;

  const settings = { ...data.settings };
  const supportedLanguages = settings && settings.supportedLanguages;
  const defaultLanguage = settings && settings.defaultLanguage;

  settings.supportedLanguages = supportedLanguages
    ? getLanguageOptionsByCodes(supportedLanguages)
    : [getBrandSupportedLanguagesOption(), ...brandSupportedLanguages];

  settings.defaultLanguage = defaultLanguage ? getLanguageOptionsByCodes([defaultLanguage])[0] || null : null;

  data.settings = settings;

  let selectedPrepaymentOption = null;
  if (data.requiresPartialDeposits) {
    selectedPrepaymentOption = 'partial-deposit';
  } else if (data.requiresPrepayment) {
    selectedPrepaymentOption = 'prepayment';
  } else if (data.allowInPersonPayments) {
    selectedPrepaymentOption = 'in-person';
  }
  data.selectedPrepaymentOption = selectedPrepaymentOption;
  data.requestCardForInPersonPayments = data.requestCardForInPersonPayments || false;

  return data;
};

export const getInitialEmptyValues = ({
  useDefaultAutoPayoutFeeValues, // TODO remove when LD_FLAGS.AUTOPAYOUT_SELF_SERVE is removed
}) => ({
  kind: 1,
  name: '',
  website: '',
  phone: '',
  email: '',
  heading: '',
  overrideBarberSettings: false,
  notifications: {
    confirm: {
      email: true,
      phone: false,
      push: true,
    },
    reschedule: {
      email: true,
      phone: false,
      push: true,
    },
    cancel: {
      email: true,
      phone: false,
      push: true,
    },
    reminder: {
      email: true,
      phone: false,
      push: true,
      time: {
        hours: 1,
        minutes: 0,
      },
    },
    arrived: {
      email: true,
      phone: false,
      push: true,
    },
  },
  schedules: makeSchedules(true),
  address: {
    street: '',
    state: '',
    city: '',
    country: '',
    zip: '',
  },
  yelpID: '',
  instagram: '',
  enabled: false,
  grossUpFees: true,
  waitingListEnabled: true,
  rewardsEnabled: true,
  kioskEnabled: true,
  chargeNoShows: true,
  productSalesToShop: false,
  prepayOnlyThreshold: 0,
  customerReminder: {
    barber_reminder: {
      email: true,
      push: true,
      time: {
        hours: 24,
        minutes: 0,
      },
    },
    reminder: {
      email: true,
      phone: true,
      push: true,
      time: {
        hours: 1,
        minutes: 0,
      },
    },
  },
  payrollKind: PAYROLL_KINDS.commissionSplits,
  payrollEnabled: false,
  dailyPayrollTipFee: useDefaultAutoPayoutFeeValues ? DEFAULT_AUTO_TIPS_FEE_PERCENT : 0,
  commissionSplitsPercentFee: useDefaultAutoPayoutFeeValues ? DEFAULT_COMMISSION_SPLIT_FEE_PERCENT : 0,
  commissionSplits: false,
  dailyPayroll: false,
  creditCardFeeEnabled: true,
  creditCardFlatFee: 30,
  creditCardPercentFee: 2.9,
  amexCreditCardFlatFee: 20,
  amexCreditCardPercentFee: 2.2,
  presentCreditCardFlatFee: 20,
  presentCreditCardPercentFee: 2.5,
  amexPresentCreditCardFlatFee: 20,
  tapToPayFlatFee: 10,
  amexPresentCreditCardPercentFee: 2.2,
  customerBookingFee: 100,
  customerBookingFeeLimitEnabled: true,
  customerBookingFeeBottomLimit: 3000,
  customerBookingFeeTopLimit: 6000,
  instantPayoutPercentageFee: 2,
  waitingListFee: 0,
  isFrenchWaiver: false,
  includeChargeLinkInNotification: true,
  applyCNPRateToChargeLink: true,
  includeTaxInTipCalculation: true,
  noExtrasForBookNoPay: true,
  legacyCustomerBookingFeeCalculation: false,
  brandName: '',
  brandId: '',
  googleEcosystem: false,
  brand: {
    name: '',
    id: '',
  },
  memberships: false,
  settings: {
    supportedLanguages: [getBrandSupportedLanguagesOption(), ...getLanguageOptionsByCodes()],
    groupAppointmentsEnabled: true,
    clientReviewsEnabled: true,
    googleReviewsNumOfAppointments: 3,
    googleReviewsTimePeriod: 12,
    googleReviewsMaxFrequency: 6,
    googleReviewsExplicit: true,
    paymentDepositsEnabled: true,
    flexCtaOnCom: false,
  },
});

export function findShopKind(shops, barbers) {
  if (shops.length) {
    return shops[0].kind;
  }
  if (barbers.length && barbers[0].shop) {
    return barbers[0].shop.kind;
  }
  return -1;
}

export function findShopName(shops, barbers) {
  if (shops.length) {
    return shops[0].name;
  }
  if (barbers.length && barbers[0].shop) {
    return barbers[0].shop.name;
  }
  return 'Unknown';
}

export function findBarberName(barbers) {
  if (barbers.length) {
    return fullName(barbers[0], true);
  }

  return null;
}

export const omitAutoPayoutSettings = (shopData) =>
  omit(shopData, [
    'commissionSplits',
    'commissionSplitsCashToBarbersServices',
    'commissionSplitsCashToBarbersProducts',
    'commissionSplitsShopPaysFees',
    'commissionPayoutDayOfWeek',
    'commissionPayoutPeriodInDays',
    'dailyPayroll',
  ]);
